import React, { lazy, Suspense } from 'react';
import { Switch, Redirect } from 'react-router-dom';
import {
  ProtectedRouteWithLoader,
  RouteWithLoader,
  RouteWithSidebar,
  Routes,
  restrictions,
} from '.';
import { Preloader } from '../components/common';

// Every page below is loaded on demand (its own JS chunk) instead of all
// being bundled into the single main.js the app used to ship on first load.
// That main bundle pulled in things like react-pdf, fullcalendar, chartist,
// react-bootstrap-table2 and sweetalert2 regardless of which page you were
// on, which is why even a page as simple as /map took forever to load.

// main pages
const Home = lazy(() => import('../pages/Home'));
const Sentiment = lazy(() => import('../pages/Sentiment'));
const ServerError = lazy(() => import('../pages/ServerError'));
const Hotspots = lazy(() => import('../pages/Hotspots'));
const Map = lazy(() => import('../pages/Map'));

// categories
const CategoriesTable = lazy(() => import('../pages/categories/CategoriesTable'));
const NewCategory = lazy(() => import('../pages/categories/NewCategory'));
const EditCategory = lazy(() => import('../pages/categories/EditCategory'));

// incidents
const EditIncident = lazy(() => import('../pages/incidents/EditIncident'));
const Incidents = lazy(() => import('../pages/incidents/Incidents'));
const IncidentsTable = lazy(() => import('../pages/incidents/IncidentsTable'));
const NewIncident = lazy(() => import('../pages/incidents/NewIncident'));
const SingleIncident = lazy(() => import('../pages/incidents/SingleIncident'));
const IncidentList = lazy(() => import('../pages/incidents/IncidentList'));

// keywords
const EditKeyword = lazy(() => import('../pages/keywords/EditKeyword'));
const NewKeyword = lazy(() => import('../pages/keywords/NewKeyword'));
const KeywordsTable = lazy(() => import('../pages/keywords/keywordsTable'));

// regions
const EditRegion = lazy(() => import('../pages/regions/EditRegion'));
const NewRegion = lazy(() => import('../pages/regions/NewRegion'));
const RegionsTable = lazy(() => import('../pages/regions/RegionsTable'));

// reports
const EditReport = lazy(() => import('../pages/reports/EditReport'));
const NewReport = lazy(() => import('../pages/reports/NewReport'));
const ReportsTable = lazy(() => import('../pages/reports/ReportsTable'));
const SingleReport = lazy(() => import('../pages/reports/SingleReport'));
const Reports = lazy(() => import('../pages/reports/Reports'));

// users
const EditUser = lazy(() => import('../pages/users/EditUser'));
const NewUser = lazy(() => import('../pages/users/NewUser'));
const UsersTable = lazy(() => import('../pages/users/UsersTable'));

// summary
const Summary = lazy(() => import('../pages/summary/Summary'));
const CreateSummary = lazy(() => import('../pages/summary/CreateSummary'));
const EditSummary = lazy(() => import('../pages/summary/EditSummary'));
const ThreatSummary = lazy(() => import('../pages/summary/ThreatSummary'));

// auth
const Signin = lazy(() => import('../pages/auth/Signin'));
const Signup = lazy(() => import('../pages/auth/Signup'));
const Forbidden = lazy(() => import('../pages/auth/Forbidden'));
const ForgotPassword = lazy(() => import('../pages/auth/ForgotPassword'));
const Settings = lazy(() => import('../pages/auth/Settings'));
const ResetPassword = lazy(() => import('../pages/auth/ResetPassword'));
const VerifyEmail = lazy(() => import('../pages/auth/VerifyEmail'));
const NotFound = lazy(() => import('../pages/auth/NotFound'));

// dashboard
const DashboardOverview = lazy(() => import('../pages/dashboard/DashboardOverview'));

// frames and campaigns
const CampaignList = lazy(() => import('../pages/campaigns/CampaignList'));
const Campaigns = lazy(() => import('../pages/campaigns/Campaigns'));
const CreateCampaign = lazy(() => import('../pages/campaigns/CreateCampaign'));
const CreateTemplate = lazy(() => import('../pages/campaigns/CreateTemplate'));
const UpdateCampaign = lazy(() => import('../pages/campaigns/UpdateCampaign'));

const AppRouter = () => {
  return (
    <Suspense fallback={<Preloader show />}>
      <Switch>
        <RouteWithLoader
          exact
          path={Routes.Signup.path}
          component={Signup}
        />
        <RouteWithLoader
          exact
          path={Routes.Forbidden.path}
          component={Forbidden}
        />
        <RouteWithLoader
          exact
          path={Routes.Sentiment.path}
          component={Sentiment}
        />
        <RouteWithLoader
          exact
          path={Routes.Campaigns.path}
          component={Campaigns}
        />
        <RouteWithLoader
          exact
          path={Routes.CreateCampaign.path}
          component={CreateCampaign}
        />
        <RouteWithLoader
          exact
          path={Routes.Campaigns.path}
          component={Campaigns}
        />
        <RouteWithLoader
          exact
          path={Routes.SingleSummary.path}
          component={ThreatSummary}
        />
        <ProtectedRouteWithLoader
          exact
          path={Routes.Home.path}
          component={Home}
          restrictedTo={restrictions.none}
        />
        <ProtectedRouteWithLoader
          exact
          path={Routes.NotFound.path}
          component={NotFound}
          restrictedTo={restrictions.none}
        />
        <ProtectedRouteWithLoader
          exact
          path={Routes.ServerError.path}
          component={ServerError}
          restrictedTo={restrictions.none}
        />
        <ProtectedRouteWithLoader
          exact
          path={Routes.Map.path}
          component={Map}
          restrictedTo={restrictions.none}
        />
        <ProtectedRouteWithLoader
          exact
          path={Routes.Reports.path}
          component={Reports}
          restrictedTo={restrictions.none}
        />
        <ProtectedRouteWithLoader
          exact
          path={Routes.IncidentsList.path}
          component={IncidentList}
          restrictedTo={restrictions.none}
        />
        <ProtectedRouteWithLoader
          exact
          path={Routes.Incidents.path}
          component={Incidents}
          restrictedTo={restrictions.none}
        />
        <ProtectedRouteWithLoader
          exact
          path={Routes.Incident.path}
          component={SingleIncident}
          restrictedTo={restrictions.none}
        />
        <ProtectedRouteWithLoader
          exact
          path={Routes.Hotspots.path}
          component={Hotspots}
          restrictedTo={restrictions.none}
        />
        <ProtectedRouteWithLoader
          exact
          path={Routes.Report.path}
          component={SingleReport}
          restrictedTo={restrictions.none}
        />
        <RouteWithLoader
          exact
          path={Routes.ForgotPassword.path}
          component={ForgotPassword}
        />
        <RouteWithLoader
          exact
          path={Routes.ResetPassword.path}
          component={ResetPassword}
        />
        <RouteWithLoader
          exact
          path={Routes.Signin.path}
          component={Signin}
        />
        <RouteWithLoader
          exact
          path={Routes.VerifyEmail.path}
          component={VerifyEmail}
        />

        <RouteWithSidebar
          exact
          path={Routes.DashboardOverview.path}
          component={DashboardOverview}
          restrictedTo={restrictions.none}
        />
        <RouteWithSidebar
          exact
          path={Routes.CreateCampaign.path}
          component={CreateCampaign}
          restrictedTo={restrictions.author}
        />
        <RouteWithSidebar
          exact
          path={Routes.UpdateCampaign.path}
          component={UpdateCampaign}
          restrictedTo={restrictions.author}
        />
        <RouteWithSidebar
          exact
          path={Routes.UpdateCampaign.path}
          component={UpdateCampaign}
          restrictedTo={restrictions.author}
        />
        <RouteWithSidebar
          exact
          path={Routes.CreateTemplate.path}
          component={CreateTemplate}
          restrictedTo={restrictions.author}
        />
        <RouteWithSidebar
          exact
          path={Routes.CampaignList.path}
          component={CampaignList}
          restrictedTo={restrictions.author}
        />
        <RouteWithSidebar
          exact
          path={Routes.Settings.path}
          component={Settings}
          restrictedTo={restrictions.none}
        />
        <RouteWithSidebar
          exact
          path={Routes.Summary.path}
          component={Summary}
          restrictedTo={restrictions.author}
        />
        <RouteWithSidebar
          exact
          path={Routes.CreateSummary.path}
          component={CreateSummary}
          restrictedTo={restrictions.author}
        />
        <RouteWithSidebar
          exact
          path={Routes.EditSummary.path}
          component={EditSummary}
          restrictedTo={restrictions.author}
        />
        <RouteWithSidebar
          exact
          path={Routes.IncidentsTable.path}
          component={IncidentsTable}
          restrictedTo={restrictions.author}
        />
        <RouteWithSidebar
          exact
          path={Routes.UsersTable.path}
          component={UsersTable}
          restrictedTo={restrictions.admin}
        />
        <RouteWithSidebar
          exact
          path={Routes.EditUser.path}
          component={EditUser}
          restrictedTo={restrictions.admin}
        />
        <RouteWithSidebar
          exact
          path={Routes.NewUser.path}
          component={NewUser}
          restrictedTo={restrictions.admin}
        />
        <RouteWithSidebar
          exact
          path={Routes.CategoriesTable.path}
          component={CategoriesTable}
          restrictedTo={restrictions.author}
        />
        <RouteWithSidebar
          exact
          path={Routes.EditCategory.path}
          component={EditCategory}
          restrictedTo={restrictions.author}
        />
        <RouteWithSidebar
          exact
          path={Routes.NewCategory.path}
          component={NewCategory}
          restrictedTo={restrictions.author}
        />
        <RouteWithSidebar
          exact
          path={Routes.ReportsTable.path}
          component={ReportsTable}
          restrictedTo={restrictions.author}
        />
        <RouteWithSidebar
          exact
          path={Routes.EditReport.path}
          component={EditReport}
          restrictedTo={restrictions.author}
        />
        <RouteWithSidebar
          exact
          path={Routes.NewReport.path}
          component={NewReport}
          restrictedTo={restrictions.author}
        />
        <RouteWithSidebar
          exact
          path={Routes.NewIncident.path}
          component={NewIncident}
          restrictedTo={restrictions.author}
        />
        <RouteWithSidebar
          exact
          path={Routes.EditIncident.path}
          component={EditIncident}
          restrictedTo={restrictions.author}
        />
        <RouteWithSidebar
          exact
          path={Routes.RegionsTable.path}
          component={RegionsTable}
          restrictedTo={restrictions.author}
        />
        <RouteWithSidebar
          exact
          path={Routes.NewRegion.path}
          component={NewRegion}
          restrictedTo={restrictions.author}
        />
        <RouteWithSidebar
          exact
          path={Routes.EditRegion.path}
          component={EditRegion}
          restrictedTo={restrictions.author}
        />
        <RouteWithSidebar
          exact
          path={Routes.KeywordsTable.path}
          component={KeywordsTable}
          restrictedTo={restrictions.author}
        />
        <RouteWithSidebar
          exact
          path={Routes.NewKeyword.path}
          component={NewKeyword}
          restrictedTo={restrictions.author}
        />
        <RouteWithSidebar
          exact
          path={Routes.EditKeyword.path}
          component={EditKeyword}
          restrictedTo={restrictions.author}
        />

        <Redirect to={Routes.NotFound.path} />
      </Switch>
    </Suspense>
  );
};

export default AppRouter;
